/**
 * The rig form, used full-page by New Rig and as the edit panel on a rig's own
 * page (no modal: a rig is a record).
 *
 * A rig is a SPEC on a unit already in Equipment: the picker lists the
 * tenant's units, and a unit that is already another rig is shown but
 * disabled (the API would 409 `rig_exists_for_equipment`). The registry number
 * is labelled CAOEC in Canada and "Contractor rig number" in the US.
 *
 * Mast rating is entered in the tenant's unit (meta.units.mastRating — daN
 * metric, lbf imperial) or kN; the API stores it as entered plus a kN copy.
 *
 * A blank field is sent as null (absent), never as 0 or "". On edit only the
 * keys that changed are sent; PATCH keeps the rest.
 */
import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { blankToNull, rigRegistryLabel, toInput } from '../../lib/well-servicing';
import type { WsCatalog, WsMeta, WsRig, WsRigWrite } from '../../types/well-servicing';
import { WS_INPUT, WS_PRIMARY, WS_SECONDARY, WsField, WsFormError } from './WsBits';

export interface RigFormState {
  equipment_id: string;
  rig_number: string;
  caoec_rig_number: string;
  rig_class: string;
  mast_rating: string;
  mast_rating_unit: string;
  notes: string;
}

export const rigFormState = (r: WsRig | null, defaultUnit: string): RigFormState => ({
  equipment_id: r?.equipmentId ?? '',
  rig_number: r?.rigNumber ?? '',
  caoec_rig_number: r?.caoecRigNumber ?? '',
  rig_class: r?.rigClass ?? '',
  mast_rating: toInput(r?.mastRating),
  mast_rating_unit: r?.mastRatingUnit ?? defaultUnit,
  notes: r?.notes ?? '',
});

/** The first thing wrong with the form, as a sentence; null when it may be sent. */
export function rigFormError(form: RigFormState): string | null {
  if (!form.equipment_id) return 'Pick the unit in Equipment this rig is.';
  if (!/[A-Za-z0-9]/.test(form.rig_number)) return 'Give the rig a number, e.g. Rig 12.';
  const m = form.mast_rating.trim();
  if (m !== '' && (!/^\d+(\.\d+)?$/.test(m) || Number(m) <= 0)) return 'Mast rating must be a number greater than 0.';
  return null;
}

/** The write body: every filled field on create, only the changed ones on edit. */
export function rigWriteBody(form: RigFormState, initial: RigFormState | null): WsRigWrite {
  const body: WsRigWrite = {};
  const changed = (k: keyof RigFormState) => !initial || form[k] !== initial[k];
  if (changed('equipment_id')) body.equipment_id = form.equipment_id;
  if (changed('rig_number')) body.rig_number = form.rig_number.trim();
  if (changed('caoec_rig_number')) body.caoec_rig_number = blankToNull(form.caoec_rig_number);
  if (changed('rig_class')) body.rig_class = form.rig_class || null;
  if (changed('mast_rating') || changed('mast_rating_unit')) {
    const m = form.mast_rating.trim();
    body.mast_rating = m === '' ? null : Number(m);
    if (m !== '') body.mast_rating_unit = form.mast_rating_unit || null;
  }
  if (changed('notes')) body.notes = blankToNull(form.notes);
  if (!initial) (Object.keys(body) as (keyof WsRigWrite)[]).forEach((k) => body[k] === null && delete body[k]);
  return body;
}

export interface EquipmentOption {
  id: string;
  name: string;
  unitNumber: string | null;
  serialNumber: string | null;
}

export function RigForm({
  rig,
  catalog,
  meta,
  equipment,
  rigs,
  pending,
  error,
  duplicateOf,
  submitLabel,
  onSubmit,
  onCancel,
}: {
  rig: WsRig | null;
  catalog: WsCatalog | null;
  meta: WsMeta | null;
  equipment: EquipmentOption[];
  /** Every rig the tenant has, so a unit that is already a rig is shown disabled. */
  rigs: WsRig[];
  pending: boolean;
  error: string | null;
  /** The existing rig a 409 named, linked under the error. */
  duplicateOf?: string | null;
  submitLabel: string;
  onSubmit: (body: WsRigWrite) => void;
  onCancel: () => void;
}) {
  const defaultUnit = meta?.units?.mastRating ?? '';
  const initial = useMemo(() => (rig ? rigFormState(rig, defaultUnit) : null), [rig, defaultUnit]);
  const [form, setForm] = useState<RigFormState>(() => rigFormState(rig, defaultUnit));
  const [localError, setLocalError] = useState<string | null>(null);
  const set = <K extends keyof RigFormState>(k: K, v: RigFormState[K]) => setForm((f) => ({ ...f, [k]: v }));
  const takenBy = useMemo(() => {
    const m = new Map<string, string>();
    rigs.forEach((r) => r.id !== rig?.id && m.set(r.equipmentId, r.rigNumber));
    return m;
  }, [rigs, rig]);
  const units = catalog?.mastRatingUnits?.length ? catalog.mastRatingUnits : defaultUnit ? [defaultUnit] : [];

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError(null);
    const problem = rigFormError(form);
    if (problem) return setLocalError(problem);
    const body = rigWriteBody(form, initial);
    if (initial && Object.keys(body).length === 0) return setLocalError('Nothing has changed.');
    onSubmit(body);
  };

  return (
    <form onSubmit={submit} className="space-y-4 px-4 py-4" data-testid="ws-rig-form" noValidate>
      <WsFormError message={localError ?? error} />
      {!localError && error && duplicateOf && (
        <p className="text-sm" data-testid="ws-rig-duplicate-link">
          <Link to={`/well-servicing/rigs/${duplicateOf}`} className="font-semibold text-fieldiq-text underline">
            Open the existing rig
          </Link>
        </p>
      )}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <WsField label="Equipment unit" hint="The rig's unit in Equipment.">
          <select className={WS_INPUT} value={form.equipment_id} onChange={(e) => set('equipment_id', e.target.value)} aria-label="Equipment unit">
            <option value="">Select a unit…</option>
            {rig && !equipment.some((x) => x.id === rig.equipmentId) && (
              <option value={rig.equipmentId}>{rig.equipmentName ?? '—'}</option>
            )}
            {equipment.map((x) => {
              const taken = takenBy.get(x.id);
              return (
                <option key={x.id} value={x.id} disabled={!!taken}>
                  {x.name}
                  {x.unitNumber ? ` · ${x.unitNumber}` : ''}
                  {taken ? ` (already ${taken})` : ''}
                </option>
              );
            })}
          </select>
        </WsField>
        <WsField label="Rig number" hint="Your own number for the rig, e.g. Rig 12.">
          <input className={WS_INPUT} maxLength={50} value={form.rig_number} onChange={(e) => set('rig_number', e.target.value)} aria-label="Rig number" />
        </WsField>
        <WsField label={rigRegistryLabel(meta)}>
          <input
            className={WS_INPUT}
            maxLength={50}
            value={form.caoec_rig_number}
            onChange={(e) => set('caoec_rig_number', e.target.value)}
            aria-label={rigRegistryLabel(meta)}
          />
        </WsField>
        <WsField label="Rig class">
          <select className={WS_INPUT} value={form.rig_class} onChange={(e) => set('rig_class', e.target.value)} aria-label="Rig class">
            <option value="">Not set</option>
            {(catalog?.rigClasses ?? []).map((c) => (
              <option key={c.value} value={c.value}>
                {c.label}
              </option>
            ))}
          </select>
        </WsField>
        <WsField label="Mast rating" hint="Blank when not known.">
          <div className="flex gap-2">
            <input
              className={WS_INPUT}
              inputMode="decimal"
              value={form.mast_rating}
              onChange={(e) => set('mast_rating', e.target.value)}
              aria-label="Mast rating"
            />
            <select
              className={`${WS_INPUT} w-24 shrink-0`}
              value={form.mast_rating_unit}
              onChange={(e) => set('mast_rating_unit', e.target.value)}
              aria-label="Mast rating unit"
            >
              {!form.mast_rating_unit && <option value="">—</option>}
              {units.map((u) => (
                <option key={u} value={u}>
                  {u}
                </option>
              ))}
            </select>
          </div>
        </WsField>
      </div>
      <WsField label="Notes">
        <textarea className={`${WS_INPUT} h-24 py-2`} value={form.notes} onChange={(e) => set('notes', e.target.value)} aria-label="Notes" />
      </WsField>
      <div className="flex flex-wrap justify-end gap-2">
        <button type="button" className={WS_SECONDARY} onClick={onCancel} disabled={pending}>
          Cancel
        </button>
        <button type="submit" className={WS_PRIMARY} disabled={pending}>
          {pending ? 'Saving…' : submitLabel}
        </button>
      </div>
    </form>
  );
}
